import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import {
  Users,
  LayoutDashboard,
  LogOut,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronRight,
  Clock,
  TrendingUp,
  UserPlus,
  Menu,
  X,
  FolderOpen,
  BarChart3,
  Calendar,
  Target,
  Layers,
  RotateCcw,
  Award,
  Sparkles,
  Settings,
  Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chart colors
const CHART_COLORS = [
  "#22c55e", "#f97316", "#3b82f6", "#eab308", "#ec4899",
  "#14b8a6", "#8b5cf6", "#ef4444", "#06b6d4", "#84cc16"
];

// NotificationBell Component
const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await authFetch(`${API}/notifications?limit=10`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unread_count || 0);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const refetchNotifications = async () => {
    try {
      const response = await authFetch(`${API}/notifications?limit=10`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await authFetch(`${API}/notifications/mark-all-read`, {
        method: "POST",
        credentials: "include",
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Error marking notifications read:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await authFetch(`${API}/notifications/${notification.notification_id}/read`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Error marking notification read:", error);
    }

    // Navigate to client if there's a client_id
    if (notification.client_id) {
      navigate(`/admin/clients/${notification.client_id}`);
    }
    setOpen(false);
    refetchNotifications();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "new_client": return "🆕";
      case "note_added": return "💬";
      case "client_update": return "📝";
      case "activity": return "📊";
      default: return "🔔";
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <button
                key={notification.notification_id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left p-3 hover:bg-slate-50 border-b last:border-0 ${
                  !notification.is_read ? "bg-blue-50/50" : ""
                }`}
              >
                <div className="flex gap-3">
                  <span className="text-lg">{getNotificationIcon(notification.notification_type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.is_read ? "font-semibold" : ""}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{notification.message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </button>
            ))
          ) : (
            <p className="text-center text-slate-500 py-8">No notifications</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    "Referral Submitted": "bg-blue-100 text-blue-800 border-blue-200",
    "Intake Complete": "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Credit Active": "bg-amber-100 text-amber-800 border-amber-200",
    "In Progress": "bg-purple-100 text-purple-800 border-purple-200",
    "Ready for Lender": "bg-[#779a86]/20 text-[#357c2a] border-[#779a86]",
    "Loan Ready": "bg-[#84c77f] text-[#357c2a] border-[#357c2a]/20",
    "Closed Won": "bg-[#84c77f] text-[#357c2a] border-[#357c2a]/20",
    "Closed Lost": "bg-red-100 text-red-800 border-red-200",
    default: "bg-[#357c2a]/10 text-[#357c2a] border-[#357c2a]/20",
  };

  return (
    <Badge
      variant="outline"
      className={statusStyles[status] || statusStyles.default}
      data-testid={`status-badge-${status?.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {status || "Unknown"}
    </Badge>
  );
};

// Donut Chart Component
const DonutChartCard = ({ title, icon: Icon, data, reportType, onViewClients, isDarkMode }) => {
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [_, count]) => sum + count, 0);
  const chartData = entries.map(([name, value], idx) => ({
    name: name.length > 20 ? name.substring(0, 20) + '...' : name,
    fullName: name,
    value,
    color: CHART_COLORS[idx % CHART_COLORS.length]
  }));

  const handleClick = (data) => {
    if (data && data.fullName) {
      onViewClients(reportType, data.fullName);
    }
  };

  return (
    <Card className={`border-0 shadow-lg rounded-2xl overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-white'}`} data-testid={`chart-${reportType}`}>
      <CardHeader className={`pb-2 transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-gradient-to-r from-slate-50 to-white'}`}>
        <div className="flex items-center justify-between">
          <CardTitle className={`text-lg font-semibold flex items-center gap-3 ${isDarkMode ? 'text-white' : ''}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-[#22c55e]/20' : 'bg-[#357c2a]/10'}`}>
              <Icon className="h-5 w-5 text-[#22c55e]" />
            </div>
            {title}
          </CardTitle>
          <Badge className="bg-[#22c55e] text-white px-3 py-1">{total} total</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {chartData.length > 0 ? (
          <div className="h-[300px] pt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="48%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  onClick={handleClick}
                  style={{ cursor: 'pointer' }}
                  stroke="none"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [value, props.payload.fullName]}
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: isDarkMode ? '1px solid #22c55e' : 'none', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    backgroundColor: isDarkMode ? '#265324' : '#fff',
                    color: isDarkMode ? '#fff' : '#000',
                    padding: '12px 16px'
                  }}
                  itemStyle={{
                    color: isDarkMode ? '#fff' : '#000'
                  }}
                  labelStyle={{
                    color: isDarkMode ? '#22c55e' : '#357c2a',
                    fontWeight: 'bold'
                  }}
                />
                <Legend 
                  layout="horizontal" 
                  align="center" 
                  verticalAlign="bottom"
                  wrapperStyle={{ 
                    fontSize: '12px', 
                    paddingTop: '12px',
                    color: isDarkMode ? '#fff' : '#64748b'
                  }}
                  formatter={(value) => <span style={{ color: isDarkMode ? '#fff' : '#374151', fontWeight: '500' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[300px] flex items-center justify-center">
            <p className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Table Card Component - Half Width
const TableCardHalf = ({ title, icon: Icon, data, reportType, onViewClients, badgeColor = "bg-[#22c55e] text-white", isDarkMode }) => {
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [_, count]) => sum + count, 0);

  return (
    <Card className={`border-0 shadow-lg rounded-2xl overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-white'}`} data-testid={`table-${reportType}`}>
      <CardHeader className={`pb-2 transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-gradient-to-r from-slate-50 to-white'}`}>
        <div className="flex items-center justify-between">
          <CardTitle className={`text-lg font-semibold flex items-center gap-3 ${isDarkMode ? 'text-white' : ''}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-[#22c55e]/20' : 'bg-[#357c2a]/10'}`}>
              <Icon className="h-5 w-5 text-[#22c55e]" />
            </div>
            {title}
          </CardTitle>
          <Badge className={`${badgeColor} px-3 py-1`}>{total} total</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {entries.length > 0 ? (
          <div className="max-h-[200px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className={isDarkMode ? 'border-[#1a3d18]' : 'border-slate-100'}>
                  <TableHead className={isDarkMode ? 'text-slate-300 font-medium' : 'text-slate-600 font-medium'}>Status</TableHead>
                  <TableHead className={`text-right ${isDarkMode ? 'text-slate-300 font-medium' : 'text-slate-600 font-medium'}`}>Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(([name, count]) => (
                  <TableRow 
                    key={name} 
                    className={`cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 border-[#1a3d18]' : 'hover:bg-slate-50 border-slate-100'}`}
                    onClick={() => onViewClients(reportType, name)}
                  >
                    <TableCell className={isDarkMode ? 'font-medium text-slate-200' : 'font-medium text-slate-700'}>{name}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className={isDarkMode ? 'bg-slate-700 text-slate-200 border-slate-600' : 'bg-slate-100 text-slate-700 border-slate-200'}>
                        {count}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center">
            <p className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Table Card Component - Full Width
const TableCardFull = ({ title, icon: Icon, data, reportType, onViewClients, badgeColor = "bg-[#779a86] text-white" }) => {
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [_, count]) => sum + count, 0);

  return (
    <Card className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden" data-testid={`table-${reportType}`}>
      <CardHeader className="pb-2 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#779a86]/20 flex items-center justify-center">
              <Icon className="h-5 w-5 text-[#357c2a]" />
            </div>
            {title}
          </CardTitle>
          <Badge className={`${badgeColor} px-3 py-1`}>{total} total</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {entries.length > 0 ? (
          <div className="max-h-[250px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100">
                  <TableHead className="text-slate-600 font-medium">Stage / Status</TableHead>
                  <TableHead className="text-slate-600 font-medium text-right">Clients</TableHead>
                  <TableHead className="text-slate-600 font-medium text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(([name, count]) => (
                  <TableRow 
                    key={name} 
                    className="cursor-pointer hover:bg-slate-50 transition-colors border-slate-100"
                    onClick={() => onViewClients(reportType, name)}
                  >
                    <TableCell className="font-medium text-slate-700">{name}</TableCell>
                    <TableCell className="text-right">
                      <Badge className="bg-[#357c2a] text-white">
                        {count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-[#357c2a] hover:text-[#357c2a] hover:bg-[#84c77f]/20">
                        View <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-slate-400">No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// AI Summary Component
const AISummaryCard = ({ summary, isLoading, userName, isDarkMode }) => (
  <Card className={`bg-gradient-to-r from-[#0d5a1a] via-[#0a3d12] to-[#050505] text-white mb-6 border-0 shadow-xl rounded-2xl`} data-testid="ai-summary-card">
    <CardContent className="pt-6 pb-6">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[#22c55e] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-1">Welcome back, {userName}! 👋</h2>
          {isLoading ? (
            <div className="space-y-2 mt-3">
              <Skeleton className="h-4 w-full bg-white/20" />
              <Skeleton className="h-4 w-3/4 bg-white/20" />
            </div>
          ) : (
            <p className="text-white/90 leading-relaxed mt-2">{summary}</p>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    location: "",
  });
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [aiSummaryLoading, setAiSummaryLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchLookups();
    fetchAISummary();
  }, []);

  useEffect(() => {
    fetchClients();
  }, [filters]);

  const fetchAISummary = async () => {
    setAiSummaryLoading(true);
    try {
      const response = await authFetch(`${API}/stats/ai-summary`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAiSummary(data.summary);
      }
    } catch (error) {
      console.error("Error fetching AI summary:", error);
      setAiSummary("Your dashboard is ready. Click on any chart to explore your client data!");
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await authFetch(`${API}/stats/dashboard`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.location) params.append("location", filters.location);

      const response = await authFetch(`${API}/clients?${params}&limit=10`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients);
        setTotalClients(data.total);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    try {
      const [locRes, statRes] = await Promise.all([
        authFetch(`${API}/lookup/locations`, { credentials: "include" }),
        authFetch(`${API}/lookup/statuses`, { credentials: "include" }),
      ]);

      if (locRes.ok) {
        const locData = await locRes.json();
        setLocations(locData.locations);
      }
      if (statRes.ok) {
        const statData = await statRes.json();
        setStatuses(statData.statuses);
      }
    } catch (error) {
      console.error("Error fetching lookups:", error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await authFetch(`${API}/sync/trigger`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Sync initiated successfully");
        setTimeout(() => {
          fetchDashboardData();
          fetchClients();
        }, 2000);
      } else {
        toast.error("Failed to trigger sync");
      }
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await authFetch(`${API}/export/clients`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data.clients, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `clients-export-${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        toast.success(`Exported ${data.count} clients`);
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed");
    }
  };

  const handleViewReportClients = async (reportType, value) => {
    // Navigate to all clients with filter applied
    const params = new URLSearchParams();
    if (reportType === "loan_ready_stage") params.append("loan_ready_stage", value);
    else if (reportType === "lead_status") params.append("lead_status", value);
    else if (reportType === "rounds") params.append("rounds", value);
    else if (reportType === "client_progress") params.append("client_progress", value);
    else if (reportType === "status") params.append("status", value);
    else if (reportType === "loan_ready_grats") params.append("loan_ready_grats", value);
    
    navigate(`/admin/clients?${params.toString()}`);
  };

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-slate-50'}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={true} />

      <main className="flex-1 min-w-0 md:ml-64">
        {/* Top Bar */}
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 backdrop-blur-md border-b transition-colors duration-300 ${isDarkMode ? 'bg-black/90 border-[#1a3d18]' : 'bg-white/90 border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 text-white' : 'hover:bg-slate-100'}`}
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className={`text-xl md:text-2xl font-bold transition-colors ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={syncing}
                data-testid="sync-btn"
                className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing..." : "Sync"}
              </Button>
              {user?.role !== "BH_LOCATION_ADMIN" && (
                <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn" className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-6">
          {/* AI Summary Card */}
          <AISummaryCard 
            summary={aiSummary} 
            isLoading={aiSummaryLoading}
            userName={user?.name?.split(" ")[0] || "Partner"}
            isDarkMode={isDarkMode}
          />

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="stat-total-clients" className={`transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200'}`}>
              <CardHeader className="pb-2">
                <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>Total Clients</CardDescription>
                <CardTitle className="text-4xl font-bold text-[#22c55e]">
                  {stats?.total_clients ?? <Skeleton className="h-10 w-20" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>All partner referrals</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-in-progress" className={`transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200'}`}>
              <CardHeader className="pb-2">
                <CardDescription className={isDarkMode ? 'text-amber-300' : ''}>In Progress</CardDescription>
                <CardTitle className={`text-4xl font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                  {stats?.in_progress ?? stats?.loan_ready_stages?.["IN LOAN READY"] ?? <Skeleton className="h-10 w-20" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>In Loan Ready Pipeline</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-loan-ready" className={`transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200'}`}>
              <CardHeader className="pb-2">
                <CardDescription className={isDarkMode ? 'text-emerald-300' : ''}>Loan Ready</CardDescription>
                <CardTitle className={`text-4xl font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  {stats?.loan_ready ?? stats?.loan_ready_stages?.["COMPLETED LOAN READY"] ?? <Skeleton className="h-10 w-20" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-xs ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Graduated / Completed</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-new-referrals" className={`transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'}`}>
              <CardHeader className="pb-2">
                <CardDescription className={isDarkMode ? 'text-blue-300' : ''}>New Referrals</CardDescription>
                <CardTitle className={`text-4xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                  {stats?.new_referrals ?? <Skeleton className="h-10 w-20" />}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Awaiting intake</p>
              </CardContent>
            </Card>
          </div>

          {/* Reports Section with Charts and Tables */}
          <div>
            <h2 className={`text-xl font-bold mb-6 flex items-center gap-3 transition-colors ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <div className="w-10 h-10 rounded-xl bg-[#22c55e] flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              Client Reports
            </h2>
            
            {/* First Row - Donut Chart + Half Table */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <DonutChartCard
                title="Lead Status & Stages"
                icon={Layers}
                data={stats?.lead_status_stages}
                reportType="lead_status"
                onViewClients={handleViewReportClients}
                isDarkMode={isDarkMode}
              />
              <TableCardHalf
                title="Loan Ready Stages"
                icon={Target}
                data={stats?.loan_ready_stages}
                reportType="loan_ready_stage"
                onViewClients={handleViewReportClients}
                badgeColor="bg-[#22c55e] text-white"
                isDarkMode={isDarkMode}
              />
            </div>
            
            {/* Second Row - Half Table */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <TableCardHalf
                title="Credit Rounds"
                icon={RotateCcw}
                data={stats?.credit_rounds}
                reportType="rounds"
                onViewClients={handleViewReportClients}
                badgeColor="bg-[#779a86] text-white"
                isDarkMode={isDarkMode}
              />
              <TableCardHalf
                title="Loan Ready Grats Stage"
                icon={Award}
                data={stats?.loan_ready_grats}
                reportType="loan_ready_grats"
                onViewClients={handleViewReportClients}
                badgeColor="bg-amber-500 text-white"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className={`card-hover transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="upcoming-deadlines-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                    <Clock className="h-5 w-5 text-amber-500" />
                    Upcoming Deadlines
                  </CardTitle>
                  <Badge variant="outline" className={`${isDarkMode ? 'text-amber-400 border-amber-600' : 'text-amber-600 border-amber-200'}`}>
                    {stats?.upcoming_deadlines?.length ?? 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {stats?.upcoming_deadlines?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.upcoming_deadlines.slice(0, 5).map((client, i) => (
                      <Link
                        key={client.client_id}
                        to={`/admin/clients/${client.client_id}`}
                        className={`flex items-center justify-between p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20' : 'hover:bg-slate-50'}`}
                      >
                        <span className={`font-medium text-sm ${isDarkMode ? 'text-slate-200' : ''}`}>
                          {client.first_name} {client.last_name}
                        </span>
                        <span className="text-sm text-amber-500">
                          {client.betenbough_deadline_date_for_loan_ready}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No upcoming deadlines</p>
                )}
              </CardContent>
            </Card>

            <Card className={`card-hover transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="recent-updates-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                    <TrendingUp className="h-5 w-5 text-teal-500" />
                    Recent Updates
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {stats?.recent_updates?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_updates.slice(0, 5).map((client, i) => (
                      <Link
                        key={client.client_id}
                        to={`/admin/clients/${client.client_id}`}
                        className={`flex items-center justify-between p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20' : 'hover:bg-slate-50'}`}
                      >
                        <span className={`font-medium text-sm ${isDarkMode ? 'text-slate-200' : ''}`}>
                          {client.first_name} {client.last_name}
                        </span>
                        <StatusBadge status={client.betenbough_client_status} />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No recent updates</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Clients can be found via All Clients page */}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
